import { useAppSelector } from "@/redux/hooks";
import { fetchProductMetadataWithGraphQL } from "@/redux/reducers/checkout/thunk";
import { useApolloClient } from "@apollo/client";
import { TrackingPixel } from "@brandclub/common-ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCheckoutUI } from "../CheckoutUIProvider";
import { CheckoutIntent } from "../types/CheckoutIntent";

export const useCheckoutCartInfo = () => {
  const [state] = useCheckoutUI();
  // @ts-ignore
  const context = state.context as any;
  const {
    checkoutIntent,
    checkoutIntentLoading,
    itemsToCheckout,
    checkoutSummaryLoading,
  } = context as {
    checkoutIntent: CheckoutIntent;
    checkoutIntentLoading: boolean;
    itemsToCheckout: {
      retailerId: number;
      retailerSku: string;
      stacklineSku: string;
      quantity: number;
    }[];
    checkoutSummaryLoading?: boolean;
  };

  const isAuthenticated = useAppSelector(
    (state) => !!state.userProfile?.userId
  );

  const retailers = useAppSelector((state) => state.retailers);

  const apolloClient = useApolloClient();

  const metadataCacheRef = useRef<Record<string, any>>({});

  const fetchProductMetadataWithCache = useCallback(
    async (skus: string[]): Promise<Record<string, any>> => {
      if (!skus.length) return [];
      const skusToFetch = skus.filter((sku) => !metadataCacheRef.current[sku]);

      if (skusToFetch.length === 0) {
        return skus.map((sku) => metadataCacheRef.current[sku]);
      }

      const newMetadata = await fetchProductMetadataWithGraphQL(
        apolloClient,
        skusToFetch,
        isAuthenticated
      );

      newMetadata.forEach((item) => {
        if (item.stacklineSku) {
          metadataCacheRef.current[item.stacklineSku] = {
            retailerId: item.retailerId,
            retailerSku: item.retailerSku,
            retailerParentSku: item.retailerParentSku,
            stacklineSku: item.stacklineSku,
            retailPrice: item.retailPrice,
            brandId: item.brandId,
            brandName: item.brandName,
            categoryId: item.categoryId,
            categoryName: item.categoryName,
            subCategoryId: item.subCategoryId,
            subCategoryName: item.subCategoryName,
            totalRewardAmount: item.totalRewardAmount,
            pixels: item.pixels,
          };
        }
      });
      return skus.reduce((acc, sku) => {
        acc[sku] = metadataCacheRef.current[sku] || null;
        return acc;
      }, {} as Record<string, any>);
    },
    [apolloClient, isAuthenticated]
  );

  const [currentCart, setCurrentCart] = useState<{
    subtotal: number;
    estimatedShipping: number;
    estimatedTax: number;
    amountToCharge: number;
    items: {
      retailerId: number;
      retailerSku: string;
      retailerParentSku: string;
      stacklineSku: string;
      title: string;
      brandId: number;
      brandName: string;
      quantity: number;
      retailPrice: number;
      pixels: TrackingPixel[];
      categoryId: number;
      categoryName: string;
      subCategoryId: number;
      subCategoryName: string;
    }[];
    deliveryEstimate: string;
    source: "checkoutIntent" | "cart";
    rewardAmounts: {
      retailerConnectRewardAmount: number;
      purchaseRewardAmount: number;
      totalRewardAmount: number;
    };
  }>({
    subtotal: 0,
    estimatedShipping: 0,
    estimatedTax: 0,
    amountToCharge: 0,
    items: [],
    deliveryEstimate: "",
    source: "checkoutIntent",
    rewardAmounts: {
      retailerConnectRewardAmount: 0,
      purchaseRewardAmount: 0,
      totalRewardAmount: 0,
    },
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCheckoutItems = async () => {
      setIsLoading(true);
      if (checkoutIntent && isAuthenticated && !checkoutIntentLoading) {
        const retailer = retailers.find(
          (retailer) => retailer.retailerId === checkoutIntent.retailerId
        );
        const productsMetadataBySku = await fetchProductMetadataWithCache(
          checkoutIntent.items?.map((item) => item.stacklineSku) || []
        );
        const purchaseRewardAmount = Object.values(
          productsMetadataBySku
        ).reduce((acc, item) => acc + item.totalRewardAmount, 0);
        setCurrentCart({
          subtotal: checkoutIntent.estimatedOrderSummary?.subtotal || 0,
          estimatedShipping:
            checkoutIntent.estimatedOrderSummary?.shippingFees || 0,
          estimatedTax: checkoutIntent.estimatedOrderSummary?.tax || 0,
          amountToCharge: checkoutIntent.estimatedOrderSummary?.total || 0,
          items:
            checkoutIntent.items?.map((item) => ({
              ...productsMetadataBySku[item.stacklineSku],
              ...item,
            })) || [],
          deliveryEstimate:
            checkoutIntent.shippingOptions?.[0]?.deliveryEstimate || "",
          rewardAmounts: {
            retailerConnectRewardAmount:
              retailer?.retailerConnectRewardAmount || 0,
            purchaseRewardAmount,
            totalRewardAmount:
              retailer?.retailerConnectRewardAmount + purchaseRewardAmount,
          },
          source: "checkoutIntent",
        });
      } else if (itemsToCheckout.length > 0) {
        const productsMetadataBySku = await fetchProductMetadataWithCache(
          itemsToCheckout.map((item) => item.stacklineSku)
        );
        const retailer = retailers.find(
          (retailer) => retailer.retailerId === itemsToCheckout[0].retailerId
        );
        const items = itemsToCheckout.map((item) => ({
          ...productsMetadataBySku[item.stacklineSku],
          ...item,
          retailPrice:
            productsMetadataBySku[item.stacklineSku]?.retailPrice || 0,
        }));
        setCurrentCart({
          items,
          subtotal: items.reduce(
            (acc, item) => acc + item.retailPrice * item.quantity,
            0
          ),
          estimatedShipping: 0,
          estimatedTax: 0,
          amountToCharge: items.reduce(
            (acc, item) => acc + item.retailPrice * item.quantity,
            0
          ),
          deliveryEstimate: "",
          rewardAmounts: {
            retailerConnectRewardAmount:
              retailer?.retailerConnectRewardAmount || 0,
            purchaseRewardAmount: Object.values(productsMetadataBySku).reduce(
              (acc, item) => acc + item.totalRewardAmount,
              0
            ),
            totalRewardAmount:
              retailer?.retailerConnectRewardAmount +
              Object.values(productsMetadataBySku).reduce(
                (acc, item) => acc + item.totalRewardAmount,
                0
              ),
          },
          source: "checkoutIntent",
        });
      } else {
        setCurrentCart({
          items: [],
          subtotal: 0,
          estimatedShipping: 0,
          estimatedTax: 0,
          amountToCharge: 0,
          deliveryEstimate: "",
          source: "checkoutIntent",
          rewardAmounts: {
            retailerConnectRewardAmount: 0,
            purchaseRewardAmount: 0,
            totalRewardAmount: 0,
          },
        });
      }
      setIsLoading(false);
    };

    fetchCheckoutItems();
  }, [
    checkoutIntent,
    itemsToCheckout,
    apolloClient,
    fetchProductMetadataWithCache,
    checkoutIntentLoading,
    retailers,
    isAuthenticated,
  ]);

  return {
    ...currentCart,
    checkoutSummaryLoading,
    isLoading,
  };
};
